import {
    GetIdpConfigsQuery,
    IdpConfig,
    GetIdpConfigsByCompanyIdQuery,
    GetIdpConfigsForUserByEmailQuery,
    IdpType
} from '../graphql/generated/autogenerated'
import { captureException } from '@thriveglobal/thrive-web-core'
import {
    auth,
    AuthConfiguration,
    SigninResult,
    SigninResultType
} from '@thriveglobal/thrive-web-auth-core'
import { Avo } from '@thriveglobal/thrive-web-tracking'

// Todo: remove these exports
export const KEYCLOAK_IDP_TYPE = IdpType.KeycloakDirectPassword
export const KEYCLOAK_SSO = IdpType.KeycloakSso
export const KEYCLOAK_SSO_AZURE = IdpType.Azuread
export const MAGIC_LINK = IdpType.Magiclink

export const supportedIdpTypes: Array<IdpType> = [
    KEYCLOAK_IDP_TYPE,
    KEYCLOAK_SSO_AZURE,
    KEYCLOAK_SSO,
    MAGIC_LINK
]

interface IIsIdpConfigEnabled {
    userIdp: IdpConfig
    filterForSignup?: boolean
    filterOutFnFOnly?: boolean
}
export function isIdpConfigEnabled({
    userIdp,
    filterForSignup,
    filterOutFnFOnly
}: IIsIdpConfigEnabled) {
    const authConfiguration = parseAuthConfiguration(userIdp.configuration)
    const idpEnabled =
        userIdp?.enabled !== false && authConfiguration.signInEnabled !== false

    if (filterForSignup) {
        if (filterOutFnFOnly && authConfiguration?.friendsAndFamilyOnly) {
            return false
        }
        if (userIdp.idpType === MAGIC_LINK) {
            return false
        } else {
            const authConfiguration = parseAuthConfiguration(
                userIdp.configuration
            )
            const signupEnabled = authConfiguration.signupEnabled !== false
            return idpEnabled && signupEnabled
        }
    }

    return idpEnabled
}

export function allIdpConfigsAreSso(idpConfigs: Array<IdpConfig>) {
    return idpConfigs.every((idpConfig) => idpConfig.idpType === KEYCLOAK_SSO)
}

function isIdpSupported(idpType: string): boolean {
    return supportedIdpTypes.some((supportedIdp) => {
        return supportedIdp === idpType
    })
}

function selectConfigWithBetterPriority(
    idpConfig1: IdpConfig,
    idpConfig2: IdpConfig
): IdpConfig {
    const config1Priority = parseAuthConfiguration(
        idpConfig1.configuration
    ).priority
    const config2Priority = parseAuthConfiguration(
        idpConfig2.configuration
    ).priority
    if (config1Priority === undefined && config2Priority === undefined) {
        return idpConfig1
    } else if (config1Priority !== undefined && config2Priority === undefined) {
        return idpConfig1
    } else if (config1Priority === undefined && config2Priority !== undefined) {
        return idpConfig2
    } else if (config1Priority === config2Priority) {
        return idpConfig1
    } else if (config1Priority > config2Priority) {
        return idpConfig2
    } else {
        return idpConfig1
    }
}

interface IIdentityFilterSupportedIdps {
    idpConfigs: Array<IdpConfig>
    filterForSignup?: boolean
    filterOutFnFOnly?: boolean
}
function identityFilterSupportedIdps({
    idpConfigs,
    filterForSignup,
    filterOutFnFOnly
}: IIdentityFilterSupportedIdps): Array<IdpConfig> {
    const idpMetadata = idpConfigs.reduce(
        (acc, userIdp) => {
            const isSupportedIdpType = isIdpSupported(userIdp.idpType)
            if (!isSupportedIdpType) {
                // return false
                return acc
            }
            if (
                isIdpConfigEnabled({
                    userIdp,
                    filterForSignup,
                    filterOutFnFOnly
                })
            ) {
                // We only want to save one idp config for each type
                if (acc[userIdp.idpType] !== null) {
                    const configWithBetterPriority =
                        selectConfigWithBetterPriority(
                            acc[userIdp.idpType],
                            userIdp
                        )
                    acc[userIdp.idpType] = configWithBetterPriority
                } else {
                    acc[userIdp.idpType] = userIdp
                }
            }
            return acc
        },
        {
            [KEYCLOAK_IDP_TYPE]: null,
            [KEYCLOAK_SSO_AZURE]: null,
            [KEYCLOAK_SSO]: null,
            [MAGIC_LINK]: null
        }
    )

    const idpMetadataWithoutDuplicates: Array<IdpConfig> = Object.values(
        idpMetadata
    ).filter((idp) => idp !== null)
    return idpMetadataWithoutDuplicates
}

interface IFilterSupportedIdps {
    idpConfigs: Array<IdpConfig>
    filterForSignup?: boolean
    filterOutFnFOnly?: boolean
}
export function filterSupportedIdps({
    idpConfigs,
    filterForSignup,
    filterOutFnFOnly
}: IFilterSupportedIdps): Array<IdpConfig> {
    return identityFilterSupportedIdps({
        idpConfigs,
        filterForSignup,
        filterOutFnFOnly
    })
}

export function parseAuthConfiguration(configJson) {
    try {
        return JSON.parse(configJson)
    } catch (error) {
        captureException(error, {
            message: 'failed to parse auth configuration json'
        })
        return {}
    }
}

export type IdpHandleResult =
    | 'IDENTITY'
    | 'IDENTITY_ERROR'
    | 'IDENTITY_SSO_AZURE_SUCCESS'
    | 'IDENTITY_SSO_AZURE_ERROR'
    | 'IDENTITY_SSO_SUCCESS'
    | 'IDENTITY_SSO_ERROR'
    | 'MAGIC_LINK'
    | 'MAGIC_LINK_ERROR'

export async function processIdpConfig(
    idpConfig: IdpConfig,
    email?: string
): Promise<IdpHandleResult> {
    if (idpConfig.idpType === KEYCLOAK_IDP_TYPE) {
        try {
            setUpIdentityClient()
        } catch (error) {
            captureException(error, {
                message: 'Failed to setup identity client'
            })
            return 'IDENTITY_ERROR'
        }
        return 'IDENTITY'
    } else if (idpConfig.idpType === KEYCLOAK_SSO) {
        try {
            setUpIdentitySsoClient(idpConfig)
            Avo.signinCompleted({
                activityType: 'user_signin',
                featureType: 'identity',
                method: 'keycloak',
                userId_: null
            })
            await identitySsoSignIn()
        } catch (error) {
            Avo.signinFailed({
                activityType: 'user_signin',
                featureType: 'identity',
                method: 'keycloak',
                reason: 'Failed to setup identity sso client'
            })
            captureException(error, {
                message: 'Failed to setup identity sso client'
            })
            return 'IDENTITY_SSO_ERROR'
        }
        return 'IDENTITY_SSO_SUCCESS'
    } else if (idpConfig.idpType === KEYCLOAK_SSO_AZURE) {
        setUpIdentityAzureClient()
        try {
            Avo.signinCompleted({
                activityType: 'user_signin',
                featureType: 'identity',
                method: 'microsoft',
                userId_: null
            })
            await identityAzureSignIn(email)
            return 'IDENTITY_SSO_AZURE_SUCCESS'
        } catch (error) {
            captureException(error, {
                message: 'Failed to setup identity azure client'
            })
            return 'IDENTITY_SSO_AZURE_ERROR'
        }
    } else if (idpConfig.idpType === MAGIC_LINK) {
        try {
            setUpMagicLink()
            await signInMagicLink(email)
            return 'MAGIC_LINK'
        } catch (error) {
            captureException(error, {
                message: 'Failed to setup identity magic link client'
            })
            return 'MAGIC_LINK_ERROR'
        }
    }
}

export interface ProcessIdpResponseProps {
    idpData:
        | GetIdpConfigsQuery
        | GetIdpConfigsByCompanyIdQuery
        | GetIdpConfigsForUserByEmailQuery
    noIdpCallback: () => void
    singleIdpCallback: () => void
    multipleIdpCallback?: () => void
    ssoCallback?: () => void
    errorCallback?: () => void
    email?: string | null
    filterForSignup?: boolean
    filterOutFnFOnly?: boolean
}

export interface ProcessIdpResponseForForgotPasswordProps {
    idpData: GetIdpConfigsForUserByEmailQuery
    noIdpCallback: () => void
    singleIdpCallback: () => void
    errorCallback: () => void
}

export function filterUsersIdps(
    idps: Array<IdpConfig>,
    userIdps: Array<String>
): Array<IdpConfig> {
    return (
        idps?.filter((idp) =>
            userIdps?.find((userIdp) => userIdp === idp?.idpType)
        ) || []
    )
}

export async function processIdpResponse({
    idpData,
    noIdpCallback,
    multipleIdpCallback,
    singleIdpCallback,
    ssoCallback,
    errorCallback,
    email,
    filterForSignup = false,
    filterOutFnFOnly = false
}: ProcessIdpResponseProps) {
    if (!idpData) {
        return
    }

    let idps = []
    if ('getIdpConfigs' in idpData) {
        idps = idpData.getIdpConfigs?.items
    } else if ('getIdpConfigsByCompanyId' in idpData) {
        idps = idpData.getIdpConfigsByCompanyId?.items
    } else if ('getIdpConfigsForUserByEmail' in idpData) {
        const userIdps = filterUsersIdps(
            idpData.getIdpConfigsForUserByEmail?.items,
            idpData.getIdpConfigsForUserByEmail?.userIdpTypes
        )
        idps = userIdps
    } else {
        return
    }

    const filteredIdps = filterSupportedIdps({
        idpConfigs: idps,
        filterForSignup,
        filterOutFnFOnly
    })
    if (filteredIdps.length === 0) {
        noIdpCallback()
    } else if (filteredIdps.length > 1) {
        if (allIdpConfigsAreSso(filteredIdps)) {
            const sortedConfigs = sortConfigsByPriority(filteredIdps)
            await processIdpConfig(sortedConfigs[0], email)
            return
        }
        multipleIdpCallback()
    } else if (filteredIdps.length === 1) {
        if (filteredIdps[0].idpType === MAGIC_LINK) {
            singleIdpCallback()
            return
        }
        const result = await processIdpConfig(filteredIdps[0], email)
        if (result === 'IDENTITY') {
            singleIdpCallback()
        } else if (result === 'IDENTITY_SSO_SUCCESS') {
            ssoCallback?.()
        } else if (
            result === 'IDENTITY_ERROR' ||
            result === 'IDENTITY_SSO_AZURE_ERROR' ||
            result === 'IDENTITY_SSO_ERROR'
        ) {
            errorCallback?.()
        }
    }
}

export async function processIdpResponseForForgotPassword({
    idpData,
    noIdpCallback,
    singleIdpCallback,
    errorCallback
}: ProcessIdpResponseForForgotPasswordProps) {
    if (!idpData) {
        return
    }
    const idps = filterUsersIdps(
        idpData.getIdpConfigsForUserByEmail?.items,
        idpData.getIdpConfigsForUserByEmail?.userIdpTypes
    )

    const filteredIdps = filterSupportedIdps({ idpConfigs: idps })
    let idpConfig
    if (filteredIdps?.length === 0) {
        noIdpCallback()
        return
    } else {
        const resetPasswordIdps = filteredIdps.filter(
            (idp) => idp.idpType === KEYCLOAK_IDP_TYPE
        )
        if (!resetPasswordIdps.length) {
            errorCallback?.()
            return
        }
        idpConfig = resetPasswordIdps[0]
    }

    const result = await processIdpConfig(idpConfig)
    if (result === 'IDENTITY') {
        singleIdpCallback()
    } else if (result === 'IDENTITY_ERROR') {
        errorCallback?.()
    }
}

export function setUpMagicLink() {
    auth.setAuthType('magicLink')
    auth.configure({} as AuthConfiguration)
}

export function setUpIdentityClient() {
    auth.setAuthType('identity')
    auth.configure({} as AuthConfiguration)
}

export function setUpIdentitySsoClient(idpConfig: IdpConfig) {
    const authConfig = parseAuthConfiguration(idpConfig.configuration)
    const idpHint = authConfig.idpHint
    auth.setAuthType('identity_sso')
    const requiredAuthConfig = {
        idpHint,
        ...(authConfig.idpId && { idp: authConfig.idpId })
    } as AuthConfiguration
    auth.configure(requiredAuthConfig)
}

export function setUpIdentityAzureClient() {
    auth.setAuthType('identity_sso')
    const requiredAuthConfig = {
        idpHint: 'microsoft'
    } as AuthConfiguration
    auth.configure(requiredAuthConfig)
}

export function identityAzureSignIn(email?: string) {
    return auth.signIn(email)
}

export function identitySsoSignIn() {
    return auth.signIn()
}

export function signInMagicLink(email: string) {
    return auth.signIn(email)
}

export const usernameAndPasswordSignin = async (
    email: string,
    password: string
): Promise<SigninResult> => {
    let signInResult = await auth.signIn(email.toLowerCase(), password)
    if (
        signInResult.resultType === SigninResultType.userNotFound &&
        email !== email.toLowerCase()
    ) {
        signInResult = await auth.signIn(email, password)
    }
    return signInResult
}

export function sortConfigsByPriority(
    configs: Array<IdpConfig>
): Array<IdpConfig> {
    return configs.sort((xIdpConfig, yIdpConfig) => {
        const xPriority = parseAuthConfiguration(
            xIdpConfig.configuration
        )?.priority
        const yPriority = parseAuthConfiguration(
            yIdpConfig.configuration
        )?.priority
        if (xPriority === undefined && yPriority === undefined) {
            // Keep original ordering
            return 0
        } else if (xPriority !== undefined && yPriority === undefined) {
            // We want all the undefined's at the end of the array so put x first
            return -1
        } else if (xPriority === undefined && yPriority !== undefined) {
            // We want all the undefined's at the end of the array so put y first
            return 1
        } else if (xPriority === yPriority) {
            return 0
        } else if (xPriority > yPriority) {
            return 1
        } else {
            return -1
        }
    })
}

export function findSsoConfig(
    idpConfigs: Array<IdpConfig>,
    filterForSignup?: boolean
): IdpConfig {
    if (!idpConfigs) {
        return null
    }
    const ssoConfigs = idpConfigs.filter((idpConfig) => {
        const authConfiguration = parseAuthConfiguration(
            idpConfig.configuration
        )
        if (authConfiguration.signInEnabled === false) {
            return false
        }
        return (
            isIdpConfigEnabled({ userIdp: idpConfig, filterForSignup }) &&
            idpConfig.idpType === KEYCLOAK_SSO
        )
    })
    if (!ssoConfigs?.length) {
        return undefined
    } else if (ssoConfigs?.length === 1) {
        return ssoConfigs[0]
    }
    const sortedIdpConfigs = sortConfigsByPriority(ssoConfigs)
    return sortedIdpConfigs[0]
}
interface IFindCredentialConfig {
    idpConfigs: Array<IdpConfig>
    filterForSignup?: boolean
    filterOutFnFOnly?: boolean
}
export function findCredentialConfig({
    idpConfigs,
    filterForSignup,
    filterOutFnFOnly
}: IFindCredentialConfig) {
    if (!idpConfigs) {
        return null
    }
    const filteredIdpConfigs = filterSupportedIdps({
        idpConfigs,
        filterForSignup,
        filterOutFnFOnly
    })
    return filteredIdpConfigs.find(
        (idpConfig) =>
            isIdpConfigEnabled({
                userIdp: idpConfig,
                filterForSignup,
                filterOutFnFOnly
            }) && idpConfig.idpType === KEYCLOAK_IDP_TYPE
    )
}

export function findMagicLinkConfig(
    idpConfigs: Array<IdpConfig>,
    filterForSignup?: boolean
) {
    if (!idpConfigs) {
        return null
    }
    const filteredIdpConfigs = filterSupportedIdps({
        idpConfigs,
        filterForSignup
    })
    return filteredIdpConfigs.find(
        (idpConfig) =>
            isIdpConfigEnabled({ userIdp: idpConfig, filterForSignup }) &&
            idpConfig.idpType === MAGIC_LINK
    )
}

export function findAzureConfig(
    idpConfigs: Array<IdpConfig>,
    filterForSignup?: boolean
) {
    if (!idpConfigs) {
        return null
    }
    return idpConfigs.find(
        (idpConfig) =>
            isIdpConfigEnabled({ userIdp: idpConfig, filterForSignup }) &&
            idpConfig.idpType === KEYCLOAK_SSO_AZURE
    )
}
